// src/dashboard/pages/teacher/ToDoList/components/EditTaskDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTasks, FaTimes, FaTrash } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  getTaskUser,
  getUserAvatarSrc,
  getUserDisplayName,
  STATUS_ORDER,
} from "../utils/toDoListDashboardUtils";

const quillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const initialForm = {
  categorie: "",
  titre: "",
  contenu: "",
  user_id: "",
  statut: "A faire",
};

export default function EditTaskDrawer({
  isOpen,
  onClose,
  task,
  onSubmit,
  onDelete,
  saving,
  deletingId,
  users = [],
  categoryOptions = [],
}) {
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    if (!isOpen || !task) return;

    setFormData({
      categorie: task.categorie || "",
      titre: task.titre || "",
      contenu: task.contenu || "",
      user_id: task.user_id ? String(task.user_id) : "",
      statut: task.statut || "A faire",
    });
  }, [isOpen, task]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        handleClose();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, saving, formData]);

  const assignedUser = useMemo(() => {
    return getTaskUser(task);
  }, [task]);

  const isDirty = useMemo(() => {
    if (!task) return false;

    return (
      String(formData.categorie || "") !== String(task.categorie || "") ||
      String(formData.titre || "") !== String(task.titre || "") ||
      String(formData.contenu || "") !== String(task.contenu || "") ||
      String(formData.user_id || "") !== String(task.user_id || "") ||
      String(formData.statut || "") !== String(task.statut || "")
    );
  }, [formData, task]);

  const canSubmit = useMemo(() => {
    return (
      task?.id &&
      String(formData.categorie || "").trim() &&
      String(formData.titre || "").trim() &&
      formData.contenu &&
      formData.user_id &&
      formData.statut &&
      !saving
    );
  }, [task?.id, formData, saving]);

  const updateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClose = () => {
    if (saving) return;

    if (
      !isDirty ||
      window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")
    ) {
      onClose?.();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    await onSubmit?.(task.id, formData);
  };

  if (!isOpen || !task) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Modification
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Modifier la tâche
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Ajustez le contenu, la catégorie, l’assignation ou le statut de
              cette tâche.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              <img
                src={getUserAvatarSrc(assignedUser)}
                alt={getUserDisplayName(assignedUser)}
                onError={(event) => {
                  event.currentTarget.src = "/img/user_avatar.jpg";
                }}
                className="h-12 w-12 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
              />

              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Assignée actuellement à
                </p>

                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {getUserDisplayName(assignedUser)}
                </p>
              </div>

              <span className="ml-auto hidden h-11 w-11 items-center justify-center rounded-2xl bg-white text-[#013E8E] shadow-sm dark:bg-slate-900 dark:text-indigo-300 sm:inline-flex">
                <FaTasks />
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Catégorie
              </label>

              <input
                type="text"
                value={formData.categorie}
                onChange={(event) =>
                  updateField("categorie", event.target.value)
                }
                list="edit-task-categories"
                placeholder="Ex : Cours, Communication, Matériel..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
                autoFocus
              />

              <datalist id="edit-task-categories">
                {categoryOptions.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Titre
              </label>

              <input
                type="text"
                value={formData.titre}
                onChange={(event) => updateField("titre", event.target.value)}
                placeholder="Ex : Préparer le planning des cours"
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Description
            </label>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <ReactQuill
                theme="snow"
                value={formData.contenu}
                onChange={(value) => updateField("contenu", value)}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Détaillez la tâche..."
                readOnly={saving}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Utilisateur assigné
              </label>

              <select
                value={formData.user_id}
                onChange={(event) => updateField("user_id", event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
              >
                <option value="">— Sélectionner —</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {getUserDisplayName(user)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Statut
              </label>

              <select
                value={formData.statut}
                onChange={(event) => updateField("statut", event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
              >
                {STATUS_ORDER.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => onDelete?.(task)}
              disabled={deletingId === task.id || saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
            >
              <FaTrash />
              Supprimer
            </button>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}